import { Button, ImprTooltip } from '@imprivata-cloud/components';
import { FileXCorner, MapPinHouse, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './AuthenticatorActionItems.less';
import { Tag } from '@imprivata-cloud/design-system';
import { FactorType } from '@/api/types';

// Row actions share one lucide icon size so they line up regardless of viewport.
const ACTION_ICON_SIZE = 20;

const nonRemovableFactors = new Set<FactorType>([FactorType.PASSWORD, FactorType.OTP]);

type AuthenticatorTableRecordProps = {
  key: string;
  factorType: FactorType;
  assuranceType?: string;
  onRemove: () => void;
  onEdit?: () => void;
  onRevokeConsent?: () => void;
  epcs?: boolean;
  onEpcsSetup?: () => void;
  /**
   * Present on every Face row (ME-11391). The action is always available: a user
   * may revisit their place of residence whenever they like, not only while the
   * backend says re-consent is due.
   */
  onUpdateBioAgreement?: () => void;
};

const AuthenticatorActionItems = (prop: AuthenticatorTableRecordProps) => {
  const { t } = useTranslation();
  return (
    <div className="auth-action-items">
      {prop.epcs && (
        <Tag className="m-2" text={t('self-service.authenticator-list.epcs')} color="swagger" />
      )}
      {!!prop.onEpcsSetup && (
        <Button
          type="link"
          className="epcs-setup-btn"
          label={t('self-service.authenticator-list.use-for-epcs')}
          onClick={prop.onEpcsSetup}
        />
      )}
      {!!prop.onUpdateBioAgreement && prop.factorType === FactorType.FACE && (
        <ImprTooltip title={t('self-service.authenticator-list.update-residence-tooltip')}>
          <Button
            type="text"
            className="update-bio-agreement-btn"
            icon={<MapPinHouse size={ACTION_ICON_SIZE} aria-hidden="true" />}
            onClick={prop.onUpdateBioAgreement}
            aria-label={t('self-service.authenticator-list.update-residence-tooltip')}
          />
        </ImprTooltip>
      )}
      {!!prop.onRevokeConsent && prop.factorType === FactorType.FACE && (
        <ImprTooltip title={t('self-service.authenticator-list.revoke-consent-tooltip')}>
          <Button
            type="text"
            className="revoke-consent-btn"
            icon={<FileXCorner size={ACTION_ICON_SIZE} aria-hidden="true" />}
            onClick={prop?.onRevokeConsent}
            aria-label={t('self-service.authenticator-list.revoke-consent-tooltip')}
          />
        </ImprTooltip>
      )}
      {prop.factorType === FactorType.PASSWORD && prop?.onEdit && (
        <ImprTooltip title={t('self-service.authenticator-list.edit-tooltip')}>
          <Button
            type="text"
            className="edit-btn"
            icon={<Pencil size={ACTION_ICON_SIZE} aria-hidden="true" />}
            onClick={prop?.onEdit}
            aria-label="Edit"
          />
        </ImprTooltip>
      )}
      {!nonRemovableFactors.has(prop.factorType) && (
        <ImprTooltip title={t('self-service.authenticator-list.remove-tooltip')}>
          <Button
            type="text"
            className="remove-btn"
            icon={<Trash2 size={ACTION_ICON_SIZE} aria-hidden="true" />}
            onClick={prop?.onRemove}
            aria-label="Remove"
          />
        </ImprTooltip>
      )}
    </div>
  );
};

export default AuthenticatorActionItems;
